import { ApiError, req } from "../api.ts";
import { commandId, outcomeMessage, PlayJournal, sessionRecovery, type Slot } from "../play/client.ts";
import { navigate } from "../router.ts";
import { t } from "../i18n/index.ts";

interface Waiting { player_id:string; account_id:string|null; slots:Slot[]; active_game:string|null }
interface Dismissed { visit:string|null; later:boolean }
interface Notice {
  slot:Slot; element:HTMLElement; title:HTMLElement; status:HTMLElement; reply:HTMLElement;
  later:HTMLButtonElement; open:HTMLButtonElement; busy:boolean; pending?:Record<string,unknown>;
}

/** Current invitations use the guest-aware Play API, not legacy challenges.
 * Poll only visible pages, without opening a readiness socket or creating a
 * session. Presence is the server's acknowledged visit to that waiting room.
 */
export function initWaitingInvitations(): void {
  const overlay = document.createElement("div"); overlay.id = "waiting-invitations"; document.body.append(overlay);
  const notices = new Map<string,Notice>();
  const dismissed = new Map<string,Dismissed>();
  let identity = "";
  let navigation = 0;
  let enabled = true, loading = false, again = false;
  const otherSide = (slot:Slot) => slot.player_id === slot.host.id ? "recipient" : "host";
  const ownSide = (slot:Slot) => slot.player_id === slot.host.id ? "host" : "recipient";
  const other = (slot:Slot) => slot.player_id === slot.host.id ? slot.recipient! : slot.host;
  const key = (id:string) => `chessme.waiting-dismissed.v1:${identity}:${id}`;
  const state = (slot:Slot):Dismissed => ({visit:slot.presence?.[otherSide(slot)] ?? null, later:!!slot.later?.[otherSide(slot)]});
  function remove(id:string) { notices.get(id)?.element.remove(); notices.delete(id); }
  function clear() { for (const id of notices.keys()) remove(id); dismissed.clear(); }
  function previous(id:string):Dismissed|undefined {
    if (!dismissed.has(id)) {
      try {
        const value = JSON.parse(sessionStorage.getItem(key(id)) ?? "null");
        if (value && (value.visit === null || typeof value.visit === "string") && typeof value.later === "boolean") dismissed.set(id,value);
      } catch { /* dismissal is optional browser state */ }
    }
    return dismissed.get(id);
  }
  function text(element:HTMLElement, value:string) { if (element.textContent !== value) element.textContent = value; }
  function paint(notice:Notice, activeGame:string|null, connected = true) {
    const slot = notice.slot, presence = state(slot), who = other(slot).name;
    const ownLater = !!slot.later?.[ownSide(slot)];
    text(notice.title, t(!connected ? "waiting.statusFor" : presence.later ? "waiting.theyAreBusy" : presence.visit ? "waiting.opponentHere" : "waiting.opponentAway", {name:who}));
    text(notice.status, !connected ? t("waiting.connectionUnknown") : [
      t(presence.visit ? "waiting.atInvitation" : "waiting.awayFromInvitation"),
      ownLater ? t("waiting.toldBusy") : activeGame ? t("waiting.currentGame") : "",
    ].filter(Boolean).join(" "));
    notice.element.dataset.presence = connected ? presence.visit ? "here" : "away" : "unknown";
    notice.element.hidden = location.pathname === `/play/slot/${slot.id}`;
    notice.later.disabled = notice.busy || (!notice.pending && ownLater) || !connected;
    text(notice.later, notice.pending && !notice.busy ? t("common.retry") : t(ownLater ? "waiting.replySent" : "waiting.later"));
    notice.open.disabled = notice.busy;
  }
  function make(slot:Slot):Notice {
    const element = document.createElement("section"); element.className = "ready-banner waiting-invitation"; element.dataset.waitingSlot = slot.id;
    const copy = document.createElement("div"); copy.className = "waiting-copy"; copy.setAttribute("role","status"); copy.setAttribute("aria-live","polite");
    const title = document.createElement("strong"), status = document.createElement("p"), reply = document.createElement("p");
    status.className = "fine"; reply.className = "waiting-reply"; copy.append(title,status,reply);
    const actions = document.createElement("div"); actions.className = "waiting-actions";
    const open = document.createElement("button"); open.type = "button"; open.className = "btn primary"; open.textContent = t("play.openInvitation");
    open.onclick = () => navigate(`/play/slot/${slot.id}`);
    const later = document.createElement("button"); later.type = "button"; later.className = "btn ghost"; later.dataset.waitingLater = "";
    const close = document.createElement("button"); close.type = "button"; close.className = "rb-close"; close.textContent = "×"; close.setAttribute("aria-label",t("ready.dismiss"));
    const notice:Notice = {slot,element,title,status,reply,later,open,busy:false};
    close.onclick = () => {
      const value = state(notice.slot); dismissed.set(slot.id,value);
      try { sessionStorage.setItem(key(slot.id),JSON.stringify(value)); } catch { /* memory still dismisses */ }
      remove(slot.id);
    };
    later.onclick = async () => {
      if (notice.busy) return;
      notice.busy = true; later.disabled = open.disabled = true; reply.textContent = "";
      const current = notice.slot;
      notice.pending ??= {actor_id:current.player_id,command_id:commandId(),expected_revision:current.revision,action:{type:"later"}};
      try {
        const result = await new PlayJournal(current.player_id,`slot:${slot.id}`).send(`/api/play/slots/${slot.id}/commands`,notice.pending);
        notice.pending = undefined;
        reply.textContent = result.outcome === "applied" && result.slot?.later?.[ownSide(current)]
          ? t("waiting.toldBusy") : outcomeMessage(result.code ?? "stale_revision");
      } catch (error) {
        reply.textContent = error instanceof Error ? error.message : t("play.failed"); sessionRecovery(element,error);
      } finally { notice.busy = false; await refresh(true); }
    };
    actions.append(open,later); element.append(copy,actions,close); overlay.append(element);
    notices.set(slot.id,notice); return notice;
  }
  async function refresh(force = false):Promise<void> {
    if (document.visibilityState !== "visible") return;
    if (force) enabled = true;
    if (!enabled) return;
    if (loading) { again ||= force; return; }
    loading = true;
    const version = navigation;
    try {
      const result = await req<Waiting>("GET","/api/play/waiting");
      if (version !== navigation) return;
      const nextIdentity = `${result.player_id}:${result.account_id ?? ""}`;
      if (identity !== nextIdentity) { clear(); identity = nextIdentity; }
      const live = new Set(result.slots.map(slot => slot.id));
      for (const id of notices.keys()) if (!live.has(id)) remove(id);
      for (const slot of result.slots) {
        const present = state(slot), closed = previous(slot.id);
        if (closed && (!present.visit || present.visit === closed.visit) && present.later === closed.later) continue;
        if (closed) {
          dismissed.delete(slot.id);
          try { sessionStorage.removeItem(key(slot.id)); } catch { /* optional dismissal persistence */ }
        }
        const notice = notices.get(slot.id) ?? ((present.visit || present.later) ? make(slot) : undefined);
        if (notice) { notice.slot = slot; paint(notice,result.active_game); }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) { enabled = false; clear(); identity = ""; }
      else for (const notice of notices.values()) paint(notice,null,false);
    } finally { loading = false; if (again) { again = false; void refresh(); } }
  }
  window.addEventListener("spa:navigated", () => {
    ++navigation;
    // A hidden waiting-room notice stays hidden until a fresh snapshot confirms
    // it is still pending; starting its game must not briefly revive the toast.
    for (const notice of notices.values()) if (location.pathname === `/play/slot/${notice.slot.id}`) notice.element.hidden = true;
    void refresh(true);
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      for (const notice of notices.values()) paint(notice,null,false);
      void refresh(true);
    }
  });
  window.setInterval(() => void refresh(),3000);
  void refresh();
}
